import React, { useEffect, useRef, useState } from "react";
import { Download, Share2, MessageCircle, Twitter, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

/**
 * One-click "Share My Cosmic Report" card.
 *
 *   <ShareCard report={numerologyReport} name={form.name} dob={form.dob} />
 *
 * Generates a 1080x1920 (Instagram-story) PNG on a canvas, then offers:
 *   - Native Web Share API with the image file (mobile + supported desktops)
 *   - WhatsApp / Twitter share intents
 *   - Direct download
 */
export default function ShareCard({ report, name, dob, label = "Share my Cosmic Report" }) {
  const canvasRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageBlob, setImageBlob] = useState(null);

  useEffect(() => {
    if (!open || !report) return;
    // Defer one frame so the dialog's portal has mounted the canvas.
    const id = requestAnimationFrame(() => drawCard());
    return () => cancelAnimationFrame(id);
  }, [open, report]); // eslint-disable-line react-hooks/exhaustive-deps

  const drawCard = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const W = 1080;
    const H = 1920;
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");

    // ---- Background gradient (deep midnight → indigo) ----
    const bg = ctx.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0, "#080a17");
    bg.addColorStop(0.55, "#0c1029");
    bg.addColorStop(1, "#1a0f2e");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    // ---- Starfield ----
    for (let i = 0; i < 240; i++) {
      const x = Math.random() * W;
      const y = Math.random() * H;
      const r = Math.random() * 1.6;
      ctx.globalAlpha = 0.3 + Math.random() * 0.7;
      ctx.fillStyle = i % 9 === 0 ? "#f5c36e" : "#ffffff";
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    // ---- Subtle gold radial behind numbers ----
    const radial = ctx.createRadialGradient(W / 2, 920, 80, W / 2, 920, 700);
    radial.addColorStop(0, "rgba(245,195,110,0.22)");
    radial.addColorStop(1, "rgba(245,195,110,0)");
    ctx.fillStyle = radial;
    ctx.fillRect(0, 0, W, H);

    // ---- Header ----
    ctx.fillStyle = "#f5c36e";
    ctx.font = "600 28px 'Inter', system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("✦  COSMIC SAGE  ✦", W / 2, 130);

    ctx.fillStyle = "rgba(255,255,255,0.55)";
    ctx.font = "400 22px 'Inter', system-ui, sans-serif";
    ctx.fillText("My Cosmic Blueprint", W / 2, 175);

    // ---- Name ----
    if (name) {
      ctx.fillStyle = "#f8fafc";
      ctx.font = "italic 600 64px 'Cormorant Garamond', Georgia, serif";
      ctx.fillText(name, W / 2, 305);
    }
    if (dob) {
      ctx.fillStyle = "rgba(255,255,255,0.7)";
      ctx.font = "400 26px 'Inter', system-ui, sans-serif";
      ctx.fillText(dob, W / 2, 355);
    }

    // ---- Driver & Conductor circles ----
    const driver = report?.driver?.number ?? "-";
    const conductor = report?.conductor?.number ?? "-";

    drawNumberOrb(ctx, W / 2 - 200, 620, 160, driver, "Driver", "#f5c36e");
    drawNumberOrb(ctx, W / 2 + 200, 620, 160, conductor, "Conductor", "#fb7185");

    // ---- Lucky numbers row ----
    ctx.fillStyle = "rgba(245,195,110,0.85)";
    ctx.font = "500 22px 'Inter', system-ui, sans-serif";
    ctx.fillText("LUCKY NUMBERS", W / 2, 1020);

    const luckyNums = report?.lucky_numbers || report?.lucky?.numbers || [];
    const luckyList = Array.isArray(luckyNums) ? luckyNums.slice(0, 6) : [];
    const startX = W / 2 - (luckyList.length - 1) * 60;
    luckyList.forEach((n, i) => {
      const cx = startX + i * 120;
      ctx.beginPath();
      ctx.arc(cx, 1100, 50, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(255,255,255,0.06)";
      ctx.fill();
      ctx.strokeStyle = "rgba(245,195,110,0.45)";
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.fillStyle = "#f8fafc";
      ctx.font = "600 42px 'Inter', system-ui, sans-serif";
      ctx.fillText(String(n), cx, 1116);
    });

    // ---- Lucky asset rows (car / house / mobile / color / gem) ----
    const first = (arr) => (Array.isArray(arr) && arr.length ? arr[0] : null);
    const list = (arr, n = 3) => (Array.isArray(arr) ? arr.slice(0, n).join(", ") : "");
    const rows = [];
    if (report?.lucky_car_number_sums?.length) rows.push(["LUCKY CAR (DIGIT SUM)", list(report.lucky_car_number_sums, 4)]);
    if (report?.lucky_house_numbers?.length) rows.push(["LUCKY HOUSE #", list(report.lucky_house_numbers, 4)]);
    if (report?.lucky_mobile_sum?.length) rows.push(["LUCKY MOBILE SUM", list(report.lucky_mobile_sum, 4)]);
    if (report?.lucky_colors?.length) rows.push(["LUCKY COLOR", first(report.lucky_colors)]);
    if (report?.lucky_gemstones?.length) rows.push(["LUCKY GEM", first(report.lucky_gemstones)]);
    if (report?.lucky_days?.length) rows.push(["LUCKY DAY", list(report.lucky_days, 2)]);

    let rowY = 1300;
    rows.slice(0, 5).forEach(([k, v]) => {
      // pill
      ctx.fillStyle = "rgba(255,255,255,0.04)";
      roundRect(ctx, 100, rowY - 36, W - 200, 76, 18);
      ctx.fill();
      ctx.strokeStyle = "rgba(245,195,110,0.18)";
      ctx.lineWidth = 1.5;
      ctx.stroke();

      ctx.textAlign = "left";
      ctx.fillStyle = "rgba(245,195,110,0.8)";
      ctx.font = "500 22px 'Inter', system-ui, sans-serif";
      ctx.fillText(k, 140, rowY + 4);

      ctx.textAlign = "right";
      ctx.fillStyle = "#f8fafc";
      ctx.font = "600 30px 'Inter', system-ui, sans-serif";
      ctx.fillText(v, W - 140, rowY + 6);

      rowY += 95;
    });

    // ---- Footer ----
    ctx.textAlign = "center";
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.font = "500 22px 'Inter', system-ui, sans-serif";
    ctx.fillText("Decode yours at  cosomicsage.com", W / 2, H - 90);

    ctx.fillStyle = "rgba(245,195,110,0.85)";
    ctx.font = "italic 400 24px 'Cormorant Garamond', Georgia, serif";
    ctx.fillText("✦  Cosmic Sage — Vedic + Numerology + AI  ✦", W / 2, H - 50);

    // Export to blob + URL
    canvas.toBlob((blob) => {
      if (!blob) return;
      setImageBlob(blob);
      setImageUrl(URL.createObjectURL(blob));
    }, "image/png", 0.95);
  };

  const handleNativeShare = async () => {
    if (!imageBlob) return;
    const file = new File([imageBlob], "cosmic-sage-report.png", { type: "image/png" });
    try {
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "My Cosmic Blueprint",
          text: `My lucky numbers from Cosmic Sage ✦ ${name || ""}`.trim(),
        });
        return;
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Couldn't open share sheet — try Download instead");
    }
    // Fallback — copy a link with title
    const text = `My Cosmic Blueprint — Driver ${report?.driver?.number}, Conductor ${report?.conductor?.number}. Decode yours at https://cosomicsage.com`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Caption copied — paste it with the downloaded image");
    } catch {
      toast.message("Use Download or one of the share buttons below");
    }
  };

  const handleDownload = () => {
    if (!imageUrl) return;
    const a = document.createElement("a");
    a.href = imageUrl;
    a.download = `cosmic-sage-${(name || "report").replace(/\s+/g, "-").toLowerCase()}.png`;
    a.click();
  };

  const waText = encodeURIComponent(
    `✦ My Cosmic Blueprint from Cosmic Sage\n\n` +
      `Driver: ${report?.driver?.number ?? "-"}\n` +
      `Conductor: ${report?.conductor?.number ?? "-"}\n` +
      (report?.lucky_numbers?.length ? `Lucky numbers: ${report.lucky_numbers.join(", ")}\n` : "") +
      (report?.lucky_colors?.length ? `Lucky color: ${report.lucky_colors[0]}\n` : "") +
      `\nDecode yours → https://cosomicsage.com`
  );

  return (
    <>
      <Button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="open-share-card"
        className="bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-600 hover:to-rose-600 text-slate-950 rounded-full font-semibold px-5 gap-2"
      >
        <Share2 className="w-4 h-4" />
        {label}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-950 border-white/10 max-w-md p-0 overflow-hidden">
          <DialogHeader className="p-5 pb-2">
            <DialogTitle className="text-slate-50 flex items-center gap-2 font-serif">
              <Sparkles className="w-4 h-4 text-gold" />
              Your Cosmic Card is Ready
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Share your numerology blueprint on Instagram, WhatsApp or X — or download the PNG.
            </DialogDescription>
          </DialogHeader>
          <div className="px-5 pb-5 space-y-4">
            <div className="rounded-xl overflow-hidden border border-white/10 bg-black">
              {/* Hidden full-size canvas; preview a downscaled <img> */}
              <canvas ref={canvasRef} className="hidden" />
              {imageUrl ? (
                <img src={imageUrl} alt="Your Cosmic Sage report" data-testid="share-card-preview" className="w-full" />
              ) : (
                <div className="aspect-[9/16] flex items-center justify-center text-slate-500 text-sm">Generating your card…</div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={handleNativeShare}
                disabled={!imageBlob}
                data-testid="share-native"
                className="bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-full font-semibold gap-2"
              >
                <Share2 className="w-4 h-4" /> Share
              </Button>
              <Button
                onClick={handleDownload}
                disabled={!imageUrl}
                data-testid="share-download"
                variant="outline"
                className="rounded-full border-white/20 text-slate-200 hover:bg-white/5 hover:text-amber-300 gap-2"
              >
                <Download className="w-4 h-4" /> Download
              </Button>
              <a
                href={`https://wa.me/?text=${waText}`}
                target="_blank"
                rel="noreferrer"
                data-testid="share-whatsapp"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 text-sm font-medium"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <a
                href={`https://twitter.com/intent/tweet?text=${waText}`}
                target="_blank"
                rel="noreferrer"
                data-testid="share-twitter"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full border border-sky-500/30 text-sky-300 hover:bg-sky-500/10 text-sm font-medium"
              >
                <Twitter className="w-4 h-4" /> X / Twitter
              </a>
            </div>

            <p className="text-[11px] text-slate-500 text-center">
              Tip: on mobile, "Share" opens Instagram, WhatsApp & Snap directly. On desktop, Download then post manually.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function drawNumberOrb(ctx, cx, cy, size, value, label, color) {
  // Outer glow
  const glow = ctx.createRadialGradient(cx, cy, size * 0.2, cx, cy, size * 1.1);
  glow.addColorStop(0, color + "55");
  glow.addColorStop(1, color + "00");
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(cx, cy, size * 1.1, 0, Math.PI * 2);
  ctx.fill();

  // Ring
  ctx.strokeStyle = color;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(cx, cy, size, 0, Math.PI * 2);
  ctx.stroke();

  // Inner fill
  ctx.fillStyle = "rgba(255,255,255,0.04)";
  ctx.beginPath();
  ctx.arc(cx, cy, size - 4, 0, Math.PI * 2);
  ctx.fill();

  // Number
  ctx.fillStyle = "#f8fafc";
  ctx.font = "700 110px 'Inter', system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(String(value), cx, cy);
  ctx.textBaseline = "alphabetic";

  // Label
  ctx.fillStyle = color;
  ctx.font = "500 24px 'Inter', system-ui, sans-serif";
  ctx.fillText(label.toUpperCase(), cx, cy + size + 38);
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
